import { MediaImageData } from "@/components/atoms/media/MediaImage";
import { CustomGrid } from "@/components/layout/grid";
import { renderMedia } from "@/utils/renderMediaComponent";
import { Box, GridItem, Heading, chakra } from "@chakra-ui/react";
import DOMPurify from "isomorphic-dompurify";
import { Swiper, SwiperSlide } from "swiper/react";
import styles from "./logoCarousel.module.scss";

import "swiper/css";

export interface LogoCarouselItem {
  id: string;
  image: MediaImageData;
}

export interface LogoCarouselProps {
  title?: string;
  items: LogoCarouselItem[];
  gap?: number;
}

export const LogoCarousel = ({ title, items }: LogoCarouselProps) => {
  const ChakraSwiper = chakra(Swiper);

  return (
    <>
      {title && (
        <CustomGrid pb={{ base: 0, md: 0 }}>
          <GridItem colStart={1} colSpan={12}>
            <Heading
              as={"h2"}
              fontSize={{ base: "2xl", md: "2rem", lg: "5xl" }}
              textAlign="left"
              fontWeight={400}
              color={"brand.aubergine"}
              sx={{
                strong: {
                  fontWeight: 400,
                  color: "brand.cyan",
                  fontFamily: "'Lausanne', sans-serif",
                },
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(title.replace(/<\/p>/g, "</br>"), {
                  ALLOWED_TAGS: ["strong", "br"],
                }),
              }}
            />
          </GridItem>
        </CustomGrid>
      )}
      <CustomGrid px="0">
        <GridItem colStart={1} colSpan={12}>
          <Box position="relative" overflow="hidden" width="100%">
            <ChakraSwiper
              spaceBetween={0}
              centerInsufficientSlides={false}
              centeredSlides={false}
              grabCursor={true}
              breakpoints={{
                320: {
                  slidesPerView: 1.15,
                },
                375: {
                  slidesPerView: 1.3,
                },
                425: {
                  slidesPerView: 1.4,
                },
                768: {
                  slidesPerView: 1.9,
                },
                1024: {
                  slidesPerView: 2.2,
                },
                1440: {
                  slidesPerView: 3.1,
                },
                1640: {
                  slidesPerView: 3.5,
                },
                1920: {
                  slidesPerView: 4.2,
                },
                2560: {
                  slidesPerView: 5,
                },
              }}
              paddingLeft={{
                base: "0.5rem !important",
                sm: "1rem !important",
                lg: "2.5rem !important",
              }}
              paddingRight={{
                base: "0.5rem !important",
                sm: "1rem !important",
                lg: "2.5rem !important",
              }}
            >
              {items.map((item, index) => (
                <SwiperSlide
                  className={`${styles["swiper-slide"]}`}
                  key={`logo-${index}`}
                >
                  <Box
                    backgroundColor="brand.grey"
                    display="flex"
                    alignContent={"center"}
                    justifyContent={"center"}
                    p={"3rem"}
                    borderRadius={8}
                    aspectRatio={1 / 1}
                    maxH="14.125rem"
                    mr={
                      index === items.length - 1
                        ? "0"
                        : { base: "2.5rem", md: "3.125rem", lg: "5rem" }
                    }
                  >
                    {renderMedia(
                      item?.image,
                      { base: "carousel" },
                      { base: 0.7 },
                      false,
                      "contain"
                    )}
                  </Box>
                </SwiperSlide>
              ))}
            </ChakraSwiper>
          </Box>
        </GridItem>
      </CustomGrid>
    </>
  );
};
